import { useSyncExternalStore } from "react";

/**
 * What VACARS5 has seen of the active flight: its phase, the event log, and
 * the route flown so far.
 *
 * All three used to be state inside the Live Tracking page, so every visit
 * started from nothing (reported 2026-10-04): the event log came back with
 * every entry stamped with the moment the pilot returned, and the route
 * history drawn on the map was gone.
 *
 * The server keeps the full track (vacanada_tracks, backend/tracks.py), and
 * FlightRecorder seeds the route from it when a flight is first watched
 * (seedTrail), so a restart or a second machine still shows the whole flight;
 * from then on the route grows here from the live position.
 *
 * It is written by FlightRecorder, which is mounted once in AppLayout and
 * watches the flight whatever page is open, and only read by Live Tracking.
 * Each flight's record is saved to localStorage (a per-machine convenience:
 * it survives a page change and a restart of VACARS5, and nothing depends on
 * it), and dropped when a different flight starts.
 */

const PREFIX = "vacars5.flight.";
const MAX_EVENTS = 80;
const MAX_TRAIL = 4000;        // ~a 10 h flight at one point per 0.25 nm of cruise
const MIN_STEP_NM = 0.25;      // closer than this to the last point adds nothing a map can show
const SAVE_EVERY_MS = 5000;

const empty = () => ({ phase: null, events: [], trail: [] });

let current = { id: null, ...empty() };
let snapshot = current;
let saveTimer = null;
const listeners = new Set();

function storage() {
  try { return window.localStorage; } catch { return null; }
}

function load(id) {
  try {
    const raw = storage()?.getItem(PREFIX + id);
    const v = raw ? JSON.parse(raw) : null;
    if (v && Array.isArray(v.events) && Array.isArray(v.trail)) {
      return { phase: v.phase ?? null, events: v.events, trail: v.trail };
    }
  } catch { /* unreadable: start this flight's record again */ }
  return empty();
}

function save() {
  saveTimer = null;
  if (current.id == null) return;
  try {
    storage()?.setItem(PREFIX + current.id, JSON.stringify({
      phase: current.phase, events: current.events, trail: current.trail,
    }));
  } catch { /* full or blocked: the in-memory record still serves this session */ }
}

function changed({ urgent = false } = {}) {
  snapshot = { ...current };
  listeners.forEach((fn) => fn());
  if (urgent) { clearTimeout(saveTimer); save(); }
  else if (!saveTimer) saveTimer = setTimeout(save, SAVE_EVERY_MS);
}

/** Forget every flight's record except `keepId`'s. */
function dropOthers(keepId) {
  const s = storage();
  if (!s) return;
  try {
    const stale = [];
    for (let i = 0; i < s.length; i += 1) {
      const k = s.key(i);
      if (k && k.startsWith(PREFIX) && k !== PREFIX + keepId) stale.push(k);
    }
    stale.forEach((k) => s.removeItem(k));
  } catch { /* nothing to tidy */ }
}

function select(id) {
  if (id == null || String(id) === String(current.id)) return;
  if (saveTimer) { clearTimeout(saveTimer); save(); }
  current = { id: String(id), ...load(String(id)) };
  dropOthers(current.id);
  changed();
}

const nm = (a, b) => {
  const r = Math.PI / 180;
  const dLat = (b[0] - a[0]) * r;
  const dLon = (b[1] - a[1]) * r;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a[0] * r) * Math.cos(b[0] * r) * Math.sin(dLon / 2) ** 2;
  return 2 * 3440.065 * Math.asin(Math.min(1, Math.sqrt(h)));
};

/**
 * Fold one look at the flight into its record. `phase` is logged only when it
 * differs from the last one logged, so a restart that finds the flight where
 * it was adds nothing. `now` is for tests.
 */
export function recordFlight(id, { phase, lat, lon } = {}, now = Date.now()) {
  if (id == null) return;
  select(id);
  let dirty = false;
  let urgent = false;

  if (phase && phase !== current.phase) {
    current.phase = phase;
    current.events = [{ ts: now, type: phase, msg: `Phase: ${phase}` }, ...current.events].slice(0, MAX_EVENTS);
    dirty = true;
    urgent = true;   // a phase change is worth keeping even if the app closes next
  }

  const la = Number(lat);
  const lo = Number(lon);
  if (Number.isFinite(la) && Number.isFinite(lo) && (la !== 0 || lo !== 0)) {
    const last = current.trail[current.trail.length - 1];
    if (!last || nm(last, [la, lo]) >= MIN_STEP_NM) {
      let trail = [...current.trail, [la, lo]];
      if (trail.length > MAX_TRAIL) {
        // Thin the older half rather than drop the departure: the start of
        // the route is as much the flight as the last hour of it.
        const half = Math.floor(trail.length / 2);
        trail = [...trail.slice(0, half).filter((_, i) => i % 2 === 0), ...trail.slice(half)];
      }
      current.trail = trail;
      dirty = true;
    }
  }

  if (dirty) changed({ urgent });
}

/**
 * Adopt the server's track (vacanada_tracks) as the route, when it knows more
 * than this machine does: VACARS5 opened mid-flight, restarted after a crash,
 * or the flight flown from another computer. Points are [lat, lon, ...].
 */
export function seedTrail(id, points) {
  if (id == null || !Array.isArray(points)) return;
  select(id);
  const pts = points
    .filter((p) => Array.isArray(p) && Number.isFinite(Number(p[0])) && Number.isFinite(Number(p[1])))
    .map((p) => [Number(p[0]), Number(p[1])]);
  if (pts.length <= current.trail.length) return;
  current.trail = pts.slice(-MAX_TRAIL);
  changed({ urgent: true });
}

/** No active flight: stop showing the last one's record (it stays saved). */
export function clearFlightView() {
  if (current.id == null) return;
  if (saveTimer) { clearTimeout(saveTimer); save(); }
  current = { id: null, ...empty() };
  changed();
}

export function getFlightSession() {
  return snapshot;
}

function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** The record of the flight being watched: { id, phase, events, trail }. */
export function useFlightSession() {
  return useSyncExternalStore(subscribe, getFlightSession, getFlightSession);
}

/** For tests only. */
export function _resetFlightSession() {
  clearTimeout(saveTimer);
  saveTimer = null;
  current = { id: null, ...empty() };
  snapshot = current;
}
